import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import {
  Calendar,
  Check,
  ClipboardList,
  ExternalLink,
  ListTodo,
  Mail,
  Search,
  Send,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { DanishDateField } from '@/components/ui/DanishDateField';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { JournalQuestionnaireChecklist } from '@/components/journal/JournalQuestionnaireChecklist';
import { QuestionnairePicker } from '@/components/questionnaires/QuestionnairePicker';
import { useClinicMembers } from '@/hooks/useClinicMembers';
import { useFormsTemplateList } from '@/hooks/useFormsTemplates';
import {
  usePatientFormsAssignments,
  useSendQuestionnaire,
  type PatientFormsAssignment,
} from '@/hooks/useClinioForms';
import { useCreateStaffTask } from '@/hooks/useStaffTasks';
import { useCourseStepQuestionnaireAllowList } from '@/hooks/useCourseStepQuestionnaireAllowList';
import { useTreatmentCourseTemplateByCatalogService } from '@/hooks/useTreatmentCourseTemplates';
import {
  allowedQuestionnaireIdsForAppointmentType,
  journalSendChecklist,
  normalizeQuestionnaireIds,
} from '@/lib/journalAppointmentQuestionnaires';
import { formatCourseDate, formatCourseDateTime } from '@/lib/courseDateFormat';
import { courseQuestionnairePrimaryScore } from '@/lib/stepSendQuestionnaires';
import { courseQuestionnaireName } from '@/lib/courseUiHelpers';
import {
  assignmentQuestionnaireName,
  indexQuestionnaireTemplates,
  looksLikeInternalTemplateId,
} from '@/lib/questionnaireDisplayName';
import {
  questionnairePickerName,
  toQuestionnairePickerItems,
} from '@/lib/questionnairePickerLabel';
import { patientHenvisningPath, patientTabPath } from '@/lib/patientRecordPaths';
import { ToolsColumn, ToolsSection } from '@/components/journal/ToolsColumn';
import type { StaffCourseStep, StaffTreatmentCourse } from '@/lib/treatmentCourseStaffView';

const ICON = 1.75;

type PanelKey = 'questionnaires' | 'scores' | 'next' | 'task';

function panelTitle(
  key: PanelKey,
  t: (key: string, opts?: Record<string, unknown>) => string,
  hasCourse: boolean,
): string {
  switch (key) {
    case 'questionnaires':
      return hasCourse ? t('treatment_courses.step_send_title') : t('journal.questionnaires');
    case 'scores':
      return t('journal.recent_scores');
    case 'next':
      return t('journal.next_time');
    case 'task':
      return t('journal.task');
    default: {
      const unexpected: never = key;
      return unexpected;
    }
  }
}

export function JournalWriteTools({
  clinicId,
  patientId,
  patientName,
  currentUserId,
  canEdit,
  course,
  step,
  serviceId,
  serviceName,
  servicePending = false,
  nextAppointment,
  onMoveNext,
  onNewAppointment,
  onCollapse,
}: {
  clinicId: string;
  patientId: string;
  patientName: string;
  currentUserId: string | null | undefined;
  canEdit: boolean;
  course: StaffTreatmentCourse | null;
  step: StaffCourseStep | null;
  /** Aftaletype on the open appointment. Null when the note is not tied to one. */
  serviceId: string | null;
  serviceName: string | null;
  /** True while the open appointment row is still loading. */
  servicePending?: boolean;
  nextAppointment: {
    id: string;
    starts_at: string;
    service_name: string | null;
    clinician_name: string | null;
    stepLabel?: string | null;
  } | null;
  onMoveNext: () => void;
  onNewAppointment: () => void;
  onCollapse: () => void;
}) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState<Record<PanelKey, boolean>>({
    questionnaires: true,
    scores: true,
    next: true,
    task: false,
  });
  const [picked, setPicked] = useState<string[]>([]);
  const [query, setQuery] = useState('');
  const [allOpen, setAllOpen] = useState(false);
  const [taskTitle, setTaskTitle] = useState('');
  const [taskAssignee, setTaskAssignee] = useState(currentUserId ?? '');
  const [taskDue, setTaskDue] = useState('');
  const sendQuestionnaire = useSendQuestionnaire();
  const createTask = useCreateStaffTask();
  const members = useClinicMembers(clinicId);
  const { data: templateLibrary, isLoading: templatesLoading } = useFormsTemplateList(clinicId);
  const assignments = usePatientFormsAssignments(patientId, clinicId);
  const formsTemplates = useMemo(
    () => [...(templateLibrary?.platform ?? []), ...(templateLibrary?.clinic ?? [])],
    [templateLibrary?.platform, templateLibrary?.clinic],
  );
  const templatesById = useMemo(
    () => indexQuestionnaireTemplates(formsTemplates),
    [formsTemplates],
  );
  const hasCourse = Boolean(course && step);
  const catalogTemplate = useTreatmentCourseTemplateByCatalogService(
    clinicId,
    hasCourse ? null : serviceId,
  );
  const appointmentAllowedIds = useMemo(
    () => allowedQuestionnaireIdsForAppointmentType(catalogTemplate.data),
    [catalogTemplate.data],
  );
  const stepAllowList = useCourseStepQuestionnaireAllowList({
    clinicId,
    course: hasCourse ? course : null,
    step: hasCourse ? step : null,
  });
  const allowedIds = hasCourse ? stepAllowList.ids : appointmentAllowedIds;

  const scoreRows = useMemo(() => {
    if (step) {
      const stepIndex = course?.steps.findIndex((row) => row.id === step.id) ?? -1;
      const previous = stepIndex > 0 ? course?.steps[stepIndex - 1] : null;
      return step.questionnaires
        .filter((row) => row.completed_at && row.score)
        .map((row) => {
          const score = courseQuestionnairePrimaryScore(row.score);
          const earlier = previous?.questionnaires.find(
            (item) => item.forms_template_id === row.forms_template_id && item.score,
          );
          const earlierScore = earlier ? courseQuestionnairePrimaryScore(earlier.score) : null;
          const delta = score != null && earlierScore != null ? score - earlierScore : null;
          return {
            id: row.forms_assignment_id ?? row.forms_template_id,
            name: courseQuestionnaireName(
              row,
              i18n.language,
              t('treatment_courses.q_unnamed'),
              templatesById,
            ),
            score,
            delta,
            previousStep: previous?.sort_order ?? null,
            date: row.completed_at,
          };
        });
    }
    const completed = (assignments.data ?? []).filter((row) => row.completed_at && row.score);
    const latestByTemplate = new Map<string, PatientFormsAssignment>();
    for (const row of completed) {
      if (!latestByTemplate.has(row.forms_template_id)) {
        latestByTemplate.set(row.forms_template_id, row);
      }
    }
    return [...latestByTemplate.values()].slice(0, 5).map((row) => ({
      id: row.id,
      name: assignmentQuestionnaireName(
        row,
        templatesById,
        i18n.language,
        t('treatment_courses.q_unnamed'),
      ),
      score: courseQuestionnairePrimaryScore(row.score),
      delta: null as number | null,
      previousStep: null as number | null,
      date: row.completed_at,
    }));
  }, [assignments.data, course?.steps, i18n.language, step, t, templatesById]);

  const libraryRows = useMemo(
    () =>
      formsTemplates
        .map((row) => ({
          id: row.id,
          name: questionnairePickerName(row, i18n.language, t('treatment_courses.q_unnamed')),
          tags: row.tags,
        }))
        .filter((row) => !looksLikeInternalTemplateId(row.name)),
    [formsTemplates, i18n.language, t],
  );
  const checklist = useMemo(
    () => journalSendChecklist({ query, allowedIds, library: libraryRows }),
    [allowedIds, libraryRows, query],
  );
  const shownChecklist = useMemo(() => {
    if (query.trim()) return checklist;
    const seen = new Set(checklist.map((row) => row.id));
    const pickedOutside = libraryRows.filter((row) => picked.includes(row.id) && !seen.has(row.id));
    return pickedOutside.length === 0 ? checklist : [...checklist, ...pickedOutside];
  }, [checklist, libraryRows, picked, query]);
  const pickerItems = useMemo(
    () =>
      toQuestionnairePickerItems(
        formsTemplates,
        i18n.language,
        t('treatment_courses.q_unnamed'),
        (count) => t('forms.picker.questions', { count }),
      ),
    [formsTemplates, i18n.language, t],
  );
  const checklistPending =
    !query.trim() &&
    (hasCourse
      ? stepAllowList.isLoading || (allowedIds.length > 0 && templatesLoading)
      : servicePending ||
        Boolean(serviceId && catalogTemplate.isLoading) ||
        (allowedIds.length > 0 && templatesLoading));

  function toggle(key: PanelKey) {
    setOpen((current) => ({ ...current, [key]: !current[key] }));
  }

  function sendMetadata(templateId: string): Record<string, string> | undefined {
    if (!step) return undefined;
    // The step workspace still hard-locks: a send tagged with the step id is
    // refused when it is outside a non-empty booked Tilladte list. Tag the
    // step only when that check would pass. Search / «Vis alle skemaer» can
    // still send anything else, attached to the appointment.
    const column = normalizeQuestionnaireIds(step.allowed_forms_template_ids);
    const locked = column.length > 0 && !column.includes(templateId);
    const metadata: Record<string, string> = {};
    if (step.appointment_id) metadata.appointment_id = step.appointment_id;
    if (!locked) metadata.treatment_course_step_id = step.id;
    return Object.keys(metadata).length > 0 ? metadata : undefined;
  }

  async function sendPicked(ids: string[]) {
    if (ids.length === 0) return;
    let sent = 0;
    try {
      for (const templateId of ids) {
        const result = await sendQuestionnaire.mutateAsync({
          patient_id: patientId,
          template_id: templateId,
          metadata: sendMetadata(templateId),
        });
        if (result.already_in_progress || result.already_sent) {
          toast(t('treatment_courses.workspace_send_already'));
          continue;
        }
        sent += 1;
      }
      if (sent === 0) return;
      setPicked([]);
      toast.success(t('treatment_courses.workspace_sent_now'));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('common.error'));
    }
  }

  async function createQuickTask() {
    const title = taskTitle.trim();
    if (!title) return;
    try {
      await createTask.mutateAsync({
        clinic_id: clinicId,
        title,
        assignee_ids: taskAssignee ? [taskAssignee] : [],
        deadline_date: taskDue || null,
        links: [{ entity_type: 'patient', entity_id: patientId }],
      });
      setTaskTitle('');
      toast.success(t('journal.task_created'));
    } catch {
      toast.error(t('common.error'));
    }
  }

  const keys: PanelKey[] = ['questionnaires', 'scores', 'next', 'task'];

  function panelIcon(key: PanelKey) {
    if (key === 'questionnaires') {
      return <ClipboardList className="h-4 w-4" strokeWidth={ICON} aria-hidden />;
    }
    if (key === 'scores') {
      return <Check className="h-4 w-4" strokeWidth={ICON} aria-hidden />;
    }
    if (key === 'next') {
      return <Calendar className="h-4 w-4" strokeWidth={ICON} aria-hidden />;
    }
    return <ListTodo className="h-4 w-4" strokeWidth={ICON} aria-hidden />;
  }

  return (
    <ToolsColumn
      title={t('journal.tools')}
      onCollapse={onCollapse}
      collapseLabel={t('journal.collapse_tools')}
    >
      {keys.map((key) => (
        <ToolsSection
          key={key}
          title={panelTitle(key, t, hasCourse)}
          icon={panelIcon(key)}
          open={open[key]}
          onToggle={() => toggle(key)}
        >
          {key === 'questionnaires' ? (
            <div>
              <Input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={t('treatment_courses.picker_search')}
                leadingIcon={<Search className="h-4 w-4" strokeWidth={ICON} aria-hidden />}
              />
              {!query.trim() && checklist.length > 0 ? (
                <p className="mt-3 text-xs font-semibold text-ds-muted">
                  {hasCourse
                    ? stepAllowList.source === 'general'
                      ? t('journal.questionnaires_from_general')
                      : t('journal.questionnaires_from_step')
                    : serviceName
                      ? t('journal.questionnaires_from_type_named', { name: serviceName })
                      : t('journal.questionnaires_from_type')}
                </p>
              ) : null}
              {!query.trim() && !checklistPending && checklist.length === 0 ? (
                <p className="mt-3 text-sm text-ds-muted" data-testid="journal-questionnaire-hint">
                  {hasCourse
                    ? stepAllowList.isError
                      ? t('journal.questionnaires_step_error')
                      : t('journal.questionnaires_step_empty')
                    : catalogTemplate.isError
                      ? t('journal.questionnaires_type_error')
                      : serviceId
                        ? t('journal.questionnaires_type_empty')
                        : t('journal.questionnaires_no_type')}
                </p>
              ) : null}
              <JournalQuestionnaireChecklist
                rows={shownChecklist}
                picked={picked}
                untaggedLabel={t('forms.no_tags', 'Ingen tags')}
                onToggle={(id) =>
                  setPicked((current) =>
                    current.includes(id) ? current.filter((item) => item !== id) : [...current, id],
                  )
                }
              />
              <p className="sp-all">
                <button type="button" className="lnk" onClick={() => setAllOpen(true)}>
                  {t('treatment_courses.step_send_show_all', 'Vis alle skemaer')}
                </button>
              </p>
              <div className="sp-f">
                <p className="via">
                  <Mail className="i xs" aria-hidden />
                  {t('treatment_courses.step_send_via_portal_email')}
                </p>
                <Button
                  type="button"
                  className="sp-send"
                  disabled={!canEdit || picked.length === 0 || sendQuestionnaire.isPending}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => void sendPicked(picked)}
                >
                  <Send className="i sm" aria-hidden />
                  {picked.length > 0
                    ? t('treatment_courses.step_send_button_count', { count: picked.length })
                    : t('treatment_courses.step_send_button')}
                </Button>
              </div>
              {allOpen ? (
                <QuestionnairePicker
                  open
                  title={t('forms.modal.title')}
                  items={pickerItems}
                  clinicId={clinicId}
                  mode="select"
                  showLists
                  showFavorites
                  initialSelectedIds={picked}
                  onClose={() => setAllOpen(false)}
                  onConfirm={(payload) => {
                    setPicked(payload.ids);
                    setAllOpen(false);
                  }}
                />
              ) : null}
            </div>
          ) : null}
          {key === 'scores' ? (
            <div>
              {scoreRows.length === 0 ? (
                <p className="text-sm text-ds-muted">{t('journal.no_scores')}</p>
              ) : (
                scoreRows.map((row) => (
                  <div key={row.id} className="jw-res">
                    <span className="nm">{row.name}</span>
                    <span className="sc">{row.score ?? '—'}</span>
                    <span className="mm">
                      {row.delta != null && row.previousStep != null
                        ? t('journal.score_since_step', {
                            delta: row.delta > 0 ? `+${row.delta}` : `${row.delta}`,
                            n: row.previousStep,
                          })
                        : t('journal.score_latest')}
                    </span>
                    <span className="dd">
                      {row.date ? formatCourseDate(row.date, i18n.language) : ''}
                    </span>
                  </div>
                ))
              )}
              <Link
                className="more mt-2 inline-flex text-sm font-medium text-ds-teal"
                to={patientTabPath(patientId, 'questionnaires')}
              >
                {t('journal.all_scores_tab')}
              </Link>
            </div>
          ) : null}
          {key === 'next' ? (
            nextAppointment ? (
              <div className="jw-nx">
                <p className="k">{t('journal.next_time')}</p>
                <p className="w">
                  {nextAppointment.stepLabel
                    ? `${nextAppointment.stepLabel} · ${formatCourseDateTime(nextAppointment.starts_at, i18n.language)}`
                    : formatCourseDateTime(nextAppointment.starts_at, i18n.language)}
                </p>
                <p className="mm">
                  {[nextAppointment.service_name, nextAppointment.clinician_name]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
                <div className="jw-nx-a">
                  <Button type="button" variant="secondary" onClick={onMoveNext}>
                    {t('treatment_courses.move_time')}
                  </Button>
                  <Button type="button" variant="tertiary" onClick={onNewAppointment}>
                    {t('journal.new_appointment')}
                  </Button>
                </div>
              </div>
            ) : (
              <div>
                <p className="text-sm font-medium">{t('journal.no_upcoming')}</p>
                <p className="mt-1 text-sm text-ds-muted">
                  {t('journal.no_upcoming_hint', { name: patientName })}
                </p>
                <Button
                  type="button"
                  variant="secondary"
                  className="mt-3"
                  onClick={onNewAppointment}
                >
                  {t('journal.new_appointment')}
                </Button>
              </div>
            )
          ) : null}
          {key === 'task' ? (
            <div className="space-y-3">
              <Input
                label={t('journal.task_title')}
                value={taskTitle}
                onChange={(event) => setTaskTitle(event.target.value)}
                placeholder={t('journal.task_title_placeholder')}
              />
              <Select
                label={t('journal.task_assign')}
                value={taskAssignee}
                onChange={(event) => setTaskAssignee(event.target.value)}
              >
                <option value="">{t('journal.task_unassigned')}</option>
                {(members.data ?? []).map((member) => (
                  <option key={member.user_id} value={member.user_id}>
                    {member.name}
                  </option>
                ))}
              </Select>
              <DanishDateField
                label={t('journal.task_due')}
                value={taskDue}
                onChange={setTaskDue}
              />
              <p className="text-xs text-ds-muted">
                {t('journal.task_hint', { name: patientName })}
              </p>
              <Button
                type="button"
                variant="secondary"
                disabled={!taskTitle.trim() || createTask.isPending}
                onClick={() => void createQuickTask()}
              >
                {t('journal.task_create')}
              </Button>
            </div>
          ) : null}
        </ToolsSection>
      ))}
      <Link className="jw-hvl" to={patientHenvisningPath(patientId)}>
        <Send className="h-4 w-4" strokeWidth={ICON} aria-hidden />
        <span>
          <b>{t('journal.refer')}</b>
          <small>{t('journal.refer_hint')}</small>
        </span>
        <ExternalLink className="ext h-4 w-4" strokeWidth={ICON} aria-hidden />
      </Link>
    </ToolsColumn>
  );
}
